/**
 * Collection infinite scroll — Yukixstone
 *
 * Appends the next page of products as the shopper scrolls, so collections read
 * as one continuous wall instead of numbered pages.
 *
 * Design notes:
 * - The theme's own pagination markup stays in the DOM untouched and keeps
 *   working as the no-JS / crawler fallback; it is only hidden once this script
 *   confirms it is alive (class added on <html>).
 * - The sentinel below the grid is rendered by `snippets/collection-infinite-scroll.liquid`
 *   and carries the next page URL, so filters and sorting are preserved for free.
 * - `FacetFiltersForm` replaces the innerHTML of #ProductGridContainer whenever
 *   filters or sorting change, so a MutationObserver re-binds to the fresh sentinel.
 */
(function () {
  'use strict';

  const CONTAINER_ID = 'ProductGridContainer';
  const GRID_SELECTOR = '#product-grid';
  const SENTINEL_SELECTOR = '[data-collection-infinite]';
  const ACTIVE_CLASS = 'collection-infinite-active';
  const PRELOAD_MARGIN = '350px 0px';
  const MIN_GAP_MS = 600;

  if (window.collectionInfiniteScrollLoaded) return;
  window.collectionInfiniteScrollLoaded = true;

  let observer = null;
  let containerObserver = null;
  let loading = false;
  let lastLoadAt = 0;

  const getContainer = () => document.getElementById(CONTAINER_ID);
  const getSentinel = () => {
    const container = getContainer();
    return container ? container.querySelector(SENTINEL_SELECTOR) : null;
  };
  const getGrid = () => {
    const container = getContainer();
    return container ? container.querySelector(GRID_SELECTOR) : null;
  };
  const getSectionId = () => {
    const grid = document.querySelector(GRID_SELECTOR);
    return grid ? grid.getAttribute('data-id') : null;
  };

  function setState(sentinel, state, message) {
    if (!sentinel) return;
    sentinel.setAttribute('data-state', state);
    sentinel.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    const text = sentinel.querySelector('[data-infinite-text]');
    if (text && typeof message === 'string' && message) text.textContent = message;
  }

  /* ---------------------------------------------------------------- loading */

  function appendPage(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incomingGrid = parsed.querySelector(GRID_SELECTOR);
    const currentGrid = getGrid();
    const container = getContainer();
    if (!incomingGrid || !currentGrid || !container) return false;

    const fragment = document.createDocumentFragment();
    Array.from(incomingGrid.children).forEach((item) => {
      const node = document.importNode(item, true);
      // the theme's reveal-on-scroll observer was set up on first paint and does
      // not know about these nodes; drop its classes so they are visible now.
      node.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
      node.removeAttribute('data-cascade');
      node.removeAttribute('style');
      node.classList.add('collection-infinite-item');
      fragment.appendChild(node);
    });
    currentGrid.appendChild(fragment);

    // swap in the freshly rendered pagination + sentinel (new next-page URL, or none)
    ['.pagination-wrapper', SENTINEL_SELECTOR].forEach((selector) => {
      const incoming = parsed.querySelector(selector);
      const current = container.querySelector(selector);
      if (!current) return;
      if (incoming) {
        current.replaceWith(document.importNode(incoming, true));
      } else {
        current.remove();
      }
    });

    return true;
  }

  function addRetry(sentinel) {
    if (!sentinel || sentinel.querySelector('[data-infinite-retry]')) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'collection-infinite__retry';
    button.setAttribute('data-infinite-retry', '');
    button.textContent = sentinel.getAttribute('data-text-retry') || 'Try again';
    button.addEventListener('click', () => {
      button.remove();
      setState(sentinel, 'idle');
      loadNext();
    });
    sentinel.appendChild(button);
  }

  async function loadNext() {
    const sentinel = getSentinel();
    if (loading || !sentinel) return;

    const nextUrl = sentinel.getAttribute('data-next-url');
    if (!nextUrl) return;

    const since = Date.now() - lastLoadAt;
    if (since < MIN_GAP_MS) {
      setTimeout(loadNext, MIN_GAP_MS - since);
      return;
    }

    loading = true;
    lastLoadAt = Date.now();
    setState(sentinel, 'loading', sentinel.getAttribute('data-text-loading'));

    const url = new URL(nextUrl, window.location.origin);
    const sectionId = getSectionId();
    if (sectionId) url.searchParams.set('section_id', sectionId);
    url.searchParams.set('_ic', String(Date.now()));

    try {
      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const html = await response.text();
      if (!appendPage(html)) throw new Error('product grid missing from response');
    } catch (error) {
      console.warn('[collection-infinite]', error);
      const current = getSentinel();
      if (current) {
        setState(current, 'error', current.getAttribute('data-text-error'));
        addRetry(current);
      }
    } finally {
      loading = false;
      attachObserver();
    }
  }

  /* --------------------------------------------------------------- observing */

  function attachObserver() {
    const sentinel = getSentinel();
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (!sentinel) return;

    document.documentElement.classList.add(ACTIVE_CLASS);

    if (sentinel.getAttribute('data-state') === 'error') return; // wait for a manual retry
    if (!sentinel.getAttribute('data-next-url')) return;
    if (!('IntersectionObserver' in window)) return;

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          loadNext();
        });
      },
      { rootMargin: PRELOAD_MARGIN }
    );
    observer.observe(sentinel);
  }

  function watchContainer() {
    const container = getContainer();
    if (!container || containerObserver) return;
    let scheduled = false;
    containerObserver = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      setTimeout(() => {
        scheduled = false;
        attachObserver();
      }, 120);
    });
    containerObserver.observe(container, { childList: true, subtree: true });
  }

  function init() {
    if (!getContainer()) return;
    attachObserver();
    watchContainer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
  }
})();
